import {
  Button,
  Combobox,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteEmployee, useEmployeesInfinite } from '@/entities/employee';
import { getFullName } from '@/shared/lib';
import { Icon, IconButton } from '@/shared/ui';

type Props = {
  employeeId: number;
  onDelete: (targetEmployeeId: number) => void;
};

export const DeleteEmployeeDialog: FC<Props> = ({ employeeId, onDelete }) => {
  const { t } = useTranslation(['common', 'employees']);
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState<string | number | undefined>(undefined);

  const { data } = useEmployeesInfinite();
  const deleteMutation = useDeleteEmployee();

  const options = useMemo(() => {
    if (!data) return [];
    return data.pages
      .flatMap((p) => p.employees)
      .filter((e) => e.id !== employeeId)
      .map((e) => ({
        value: e.id,
        label: getFullName(e.firstName, e.lastName, e.middleName),
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'ru'));
  }, [data, employeeId]);

  const handleConfirm = () => {
    if (!targetId) return;
    deleteMutation.mutate(
      { id: employeeId, targetEmployeeId: Number(targetId) },
      {
        onSuccess: () => {
          setOpen(false);
          onDelete(Number(targetId));
        },
      },
    );
  };

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <IconButton
        icon={<Icon type="trash" />}
        color="destructive"
        label={t('employees:deleteEmployee')}
        trigger={DialogTrigger}
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('employees:deleteTitle')}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{t('employees:deleteDescription')}</DialogDescription>
        <div className="py-4">
          <Combobox
            label={t('employees:deleteSelectTarget')}
            options={options}
            value={targetId}
            onChange={(val) => setTargetId(val)}
            required
            menu={{ className: 'max-h-60 overflow-y-auto', portal: { enable: false } }}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('common:cancel')}</Button>
          </DialogClose>
          <Button
            color="destructive"
            variant="outline"
            onClick={handleConfirm}
            isLoading={deleteMutation.isPending}
            disabled={!targetId}
          >
            {t('common:confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
